'use client';

import { useEffect } from 'react';
import Script from 'next/script';
import { QueryClientProvider } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import FeatureFlagProvider from '~/components/providers/FeatureFlagProvider';
import KYCProvider from '~/components/providers/KYCProvider';
import LocationProvider from '~/components/providers/LocationProvider';
import UserProvider from '~/components/providers/UserProvider';
import TranslationsProvider from '~/components/providers/TranslationsProvider';
import queryClient from '~/config/initReactQuery';
import { SignUpModalContextProvider } from '~/domains/common/context/SignUpModalContext';
import { MaintenanceWrapper } from '~/domains/app/components/MaintenanceWrapper/MaintenanceWrapper';
import { TimeoutWrapper } from '~/domains/app/components/TimeoutWrapper/TimeoutWrapper';
import RootModalsContextProvider from '~/domains/common/context/RootModalsContext/RootModalsContextProvider';
import { GamificationContextProvider } from '~/domains/promotions/domains/gamification/context/GamificationContext';
import { GamecenterPrevPathContextProvider } from '~/domains/contest/domains/gamecenter/domains/common/context/GamecenterPrevPathContext/GamecenterPrevPathContext';
import { QuickPickSlipDetailPrevPathContextProvider } from '~/domains/contest/domains/quickPick/context/QuickPickSlipDetailPrevPathContext/QuickPickSlipDetailPrevPathContext';
import { clearLegacySocurePayload } from '~/domains/kyc/utils/legacySocurePayload';
import isWebView from '~/utils/isWebView';

import { PrevLocationContextProvider } from '../context/PrevLocationContext';

import { AppNotificationsProvider } from './AppNotificationsProvider';
import SingleUseTokenProvider from './SingleUseTokenProvider';
import { SocureSdkScripts } from './SocureSdkScripts';
import { installOptOutNoticeControl } from './optOutNotice';

const ROUTES_WITHOUT_COOKIE_BANNER = ['/account/deposit/vipp', '/account/withdrawal/vipp'];

export function AppProviders({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const showCookieBanner = !ROUTES_WITHOUT_COOKIE_BANNER.includes(router.route) && !isWebView();

  useEffect(() => {
    clearLegacySocurePayload();
  }, []);

  // Tame Osano's "opt-out honored" pill (hide-for-30-days CTA + TTL-bounded
  // dismissal) only while the cookie banner (Osano) is actually mounted.
  useEffect(() => {
    if (!showCookieBanner) return undefined;
    return installOptOutNoticeControl();
  }, [showCookieBanner]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Osano = Cookie consent */}
      {showCookieBanner && (
        <Script src="https://cmp.osano.com/16BVIfT9cJhAu25Hp/66d19d54-6055-460a-adf1-0016fe26af65/osano.js" />
      )}
      <TranslationsProvider>
        <AppNotificationsProvider>
          <PrevLocationContextProvider>
            <GamecenterPrevPathContextProvider>
              <QuickPickSlipDetailPrevPathContextProvider>
                <SignUpModalContextProvider>
                  <UserProvider>
                    <SingleUseTokenProvider>
                      <FeatureFlagProvider>
                        <MaintenanceWrapper>
                          <TimeoutWrapper>
                            {/* GeoComply = Location SaaS */}
                            <LocationProvider>
                              {/* Socure = KYC SaaS */}
                              <SocureSdkScripts />
                              <KYCProvider>
                                {/* Paysafe = Payments SaaS */}
                                <Script src="https://hosted.paysafe.com/js/v1/latest/paysafe.min.js" />
                                <Script src="https://hosted.paysafe.com/checkout/v2/paysafe.checkout.min.js" />

                                <RootModalsContextProvider>
                                  <GamificationContextProvider>
                                    {children}
                                  </GamificationContextProvider>
                                </RootModalsContextProvider>
                              </KYCProvider>
                            </LocationProvider>
                          </TimeoutWrapper>
                        </MaintenanceWrapper>
                      </FeatureFlagProvider>
                    </SingleUseTokenProvider>
                  </UserProvider>
                </SignUpModalContextProvider>
              </QuickPickSlipDetailPrevPathContextProvider>
            </GamecenterPrevPathContextProvider>
          </PrevLocationContextProvider>
        </AppNotificationsProvider>
      </TranslationsProvider>
      {/*  **ONLY FOR DEVELOPMENT** Docs says it won't build in prod */}
      {/* <ReactQueryDevtools /> */}
    </QueryClientProvider>
  );
}
